/**
 * Every alert rule with its Alert Actions, one row each: Dashboard / Email / Message API
 * switches, who sends them, and the recipients. Switches save at once; turning on a channel
 * that has no recipients yet opens the editor instead (it would fail validation otherwise).
 */
import { useEffect, useState } from 'react';
import {
  PencilSquareIcon,
  BuildingOffice2Icon,
  CloudIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { Callout } from '../common/Callout';
import { AASwitch } from './AlertActionsControls';
import RuleNotificationFields from './RuleNotificationFields';
import {
  alertActionsErrorText,
  isDashboardOn,
  pickRuleNotifications,
  ruleNotificationProblem,
  splitRecipients,
  viaLabel,
  type RuleNotificationValue,
} from './alertActionsModel';

export interface AlertActionRule extends RuleNotificationValue {
  id: string;
  name?: string | null;
  is_active?: boolean;
  event_type?: string | null;
  severity?: string | null;
  camera_id?: string | null;
  group_id?: string | null;
  [key: string]: any;
}

export interface RuleNotificationTableProps {
  rules: AlertActionRule[];
  loading?: boolean;
  canEdit: boolean;
  perspective?: 'site' | 'central';
  /** Rule title (name or a generated one). */
  titleOf: (rule: AlertActionRule) => string;
  /** One line under the title — e.g. "Intrusion · Gate camera · critical". */
  describe?: (rule: AlertActionRule) => string;
  /** Save these Alert Actions fields on the rule; resolves with the updated rule. */
  onPatch: (rule: AlertActionRule, patch: ReturnType<typeof pickRuleNotifications>) => Promise<AlertActionRule | void>;
  cloudAvailable?: boolean | null;
  cloudReason?: string | null;
  emptyText?: string;
}

const TH = 'whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';

function Recipients({ rule }: { rule: AlertActionRule }) {
  const email = rule.email_enabled ? splitRecipients(rule.email_recipients) : [];
  const msg = rule.message_api_enabled ? splitRecipients(rule.message_api_recipients) : [];
  if (!email.length && !msg.length) return <span className="text-[12px] text-muted-foreground">—</span>;
  const line = (Icon: typeof EnvelopeIcon, list: string[]) => (
    <span className="flex min-w-0 items-center gap-1 text-[12px] text-foreground" title={list.join(', ')}>
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{list[0]}</span>
      {list.length > 1 && <span className="shrink-0 rounded bg-muted px-1 text-[10px] text-muted-foreground">+{list.length - 1}</span>}
    </span>
  );
  return (
    <div className="flex max-w-[16rem] flex-col gap-0.5">
      {email.length > 0 && line(EnvelopeIcon, email)}
      {msg.length > 0 && line(ChatBubbleLeftRightIcon, msg)}
    </div>
  );
}

function ViaPill({ rule, perspective }: { rule: AlertActionRule; perspective: 'site' | 'central' }) {
  const cloud = rule.notify_via === 'cloud';
  const sendsOut = !!rule.email_enabled || !!rule.message_api_enabled;
  const Icon = cloud ? CloudIcon : BuildingOffice2Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
        sendsOut ? 'border-border text-foreground' : 'border-border text-muted-foreground opacity-60'
      }`}
      style={cloud && sendsOut ? { color: 'var(--info)', borderColor: 'color-mix(in srgb, var(--info) 40%, transparent)' } : undefined}
    >
      <Icon className="h-3.5 w-3.5" />
      {viaLabel(rule.notify_via, perspective)}
    </span>
  );
}

export function RuleNotificationModal({
  open,
  rule,
  titleOf,
  perspective = 'site',
  cloudAvailable,
  cloudReason,
  onSave,
  onClose,
}: {
  open: boolean;
  rule: AlertActionRule | null;
  titleOf: (rule: AlertActionRule) => string;
  perspective?: 'site' | 'central';
  cloudAvailable?: boolean | null;
  cloudReason?: string | null;
  onSave: (rule: AlertActionRule, patch: ReturnType<typeof pickRuleNotifications>) => Promise<unknown>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<RuleNotificationValue>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && rule) {
      setForm({ ...rule });
      setError('');
      setSaving(false);
    }
  }, [open, rule]);

  const problem = ruleNotificationProblem(form);

  async function save() {
    if (!rule) return;
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(rule, pickRuleNotifications(form));
      onClose();
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Notifications for this rule"
      subtitle={rule ? titleOf(rule) : undefined}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 flex-1 truncate text-[12px] text-destructive">{error || problem}</span>
          <div className="flex shrink-0 items-center gap-2">
            <button onClick={onClose} disabled={saving} className="rounded-lg border border-border px-4 py-2 text-[13px] text-muted-foreground hover:bg-muted disabled:opacity-50">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || !!problem}
              className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      }
    >
      <div className="p-5">
        <RuleNotificationFields
          value={form}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
          perspective={perspective}
          cloudAvailable={cloudAvailable}
          cloudReason={cloudReason}
        />
      </div>
    </Modal>
  );
}

export default function RuleNotificationTable({
  rules,
  loading,
  canEdit,
  perspective = 'site',
  titleOf,
  describe,
  onPatch,
  cloudAvailable,
  cloudReason,
  emptyText = 'No alert rules yet — create them under Alerts → Alert Rules.',
}: RuleNotificationTableProps) {
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<AlertActionRule | null>(null);
  const [error, setError] = useState('');

  async function patch(rule: AlertActionRule, changes: Partial<RuleNotificationValue>) {
    const next = { ...rule, ...changes };
    if (ruleNotificationProblem(next)) {
      // e.g. Email switched on with no recipients — ask for them instead of failing.
      setEditing(next);
      return;
    }
    setBusy((b) => ({ ...b, [rule.id]: true }));
    setError('');
    try {
      await onPatch(rule, pickRuleNotifications(next));
    } catch (err) {
      setError(`${titleOf(rule)}: ${alertActionsErrorText(err, 'could not save')}`);
    } finally {
      setBusy((b) => ({ ...b, [rule.id]: false }));
    }
  }

  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {[0, 1, 2].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-muted" />)}
      </div>
    );
  }

  return (
    <div>
      {error && <Callout tone="danger" className="m-3">{error}</Callout>}
      {rules.length === 0 ? (
        <p className="px-4 py-10 text-center text-[13px] text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-border bg-muted">
              <tr>
                <th className={TH}>Alert rule</th>
                <th className={`${TH} text-center`}>Dashboard</th>
                <th className={`${TH} text-center`}>Email</th>
                <th className={`${TH} text-center`}>Message API</th>
                <th className={TH}>Sent by</th>
                <th className={TH}>Recipients</th>
                <th className={TH}><span className="sr-only">Edit</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rules.map((rule) => {
                const b = !!busy[rule.id];
                const off = rule.is_active === false;
                return (
                  <tr key={rule.id} className="hover:bg-muted">
                    <td className="max-w-[22rem] px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className={`truncate text-[13px] font-medium ${off ? 'text-muted-foreground' : 'text-foreground'}`}>{titleOf(rule)}</span>
                        {off && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Rule off</span>}
                      </div>
                      {describe && <div className="truncate text-[11px] text-muted-foreground">{describe(rule)}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex justify-center">
                        <AASwitch checked={isDashboardOn(rule)} busy={b} disabled={!canEdit} label="Dashboard" onChange={(v) => patch(rule, { websocket_enabled: v })} />
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex justify-center">
                        <AASwitch checked={!!rule.email_enabled} busy={b} disabled={!canEdit} label="Email" onChange={(v) => patch(rule, { email_enabled: v })} />
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex justify-center">
                        <AASwitch checked={!!rule.message_api_enabled} busy={b} disabled={!canEdit} label="Message API" onChange={(v) => patch(rule, { message_api_enabled: v })} />
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      {canEdit ? (
                        <button
                          type="button"
                          disabled={b}
                          onClick={() => patch(rule, { notify_via: rule.notify_via === 'cloud' ? 'local' : 'cloud' })}
                          title="Switch who sends this rule's email and messages"
                          className="disabled:opacity-50"
                        >
                          <ViaPill rule={rule} perspective={perspective} />
                        </button>
                      ) : (
                        <ViaPill rule={rule} perspective={perspective} />
                      )}
                    </td>
                    <td className="px-3 py-2.5"><Recipients rule={rule} /></td>
                    <td className="px-3 py-2.5 text-right">
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => setEditing(rule)}
                          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          aria-label={`Edit notifications for ${titleOf(rule)}`}
                          title="Edit notifications"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <RuleNotificationModal
        open={!!editing}
        rule={editing}
        titleOf={titleOf}
        perspective={perspective}
        cloudAvailable={cloudAvailable}
        cloudReason={cloudReason}
        onSave={onPatch}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}
